import {Injectable} from '@angular/core';
import {Subscription} from "rxjs";
import {map, mergeMap} from "rxjs/operators";
import {MediaObjectRestService} from "../../api/services/media-object.rest.service";
import {PostRestService} from "../../api/services/post-rest.service";
import {MediaObjectApiModel} from "../../api/models/media-object.api-model";
import {PostApiModel} from "../../api/models/post-api.model";
import {PostViewModel} from "../../shared/timeline/post.view-model";
import {PostTypeEnum} from "../enums/post-type.enum";

export interface QueueMediaPostParams {
  file: File;
  caption: string;
  thumbnailFile?: File | null;
  thumbnailTime?: number | null;
  filterPreset?: string | null;
  isVideo: boolean;
  projectIri?: string | null;
  eventIri?: string | null;
  onSuccess?: (post: PostViewModel) => void;
  onError?: (error: unknown) => void;
}

export interface UploadedMediaPostParams {
  mediaIri: string;
  caption: string;
  projectIri?: string | null;
  eventIri?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PostPublishOrchestratorService {
  private readonly inFlight = new Map<string, Subscription>();

  constructor(
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly postRS: PostRestService,
  ) {
  }

  public queueMediaPost(params: QueueMediaPostParams): string {
    const taskId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const mediaUpload$ = params.isVideo
      ? this.mediaObjectRS.uploadVideo(params.file, null, params.thumbnailFile || undefined, params.thumbnailTime ?? undefined, params.filterPreset ?? null)
      : this.mediaObjectRS.uploadImage(params.file, null, params.filterPreset ?? null);

    const sub = mediaUpload$.pipe(
      mergeMap((mediaObjectAM: MediaObjectApiModel) => {
        const postAM = new PostApiModel();
        postAM.media = [mediaObjectAM['@id']];
        postAM.content = params.caption || '';

        if (params.projectIri) {
          postAM.project = params.projectIri;
          postAM.type = PostTypeEnum.PROJECT_MEDIA_POST;
        }

        if (params.eventIri) {
          postAM.event = params.eventIri;
          postAM.type = PostTypeEnum.EVENT_MEDIA_POST;
        }
        return this.postRS.create(postAM).pipe(map((postResponse: PostApiModel) => new PostViewModel(postResponse)));
      })
    ).subscribe({
      next: (post) => params.onSuccess?.(post),
      error: (error) => params.onError?.(error),
      complete: () => {
        this.inFlight.delete(taskId);
      },
    });

    this.inFlight.set(taskId, sub);
    return taskId;
  }

  /** Creates the post after a native uploader has streamed the media file. */
  public createPostForUploadedMedia(params: UploadedMediaPostParams) {
    const postAM = new PostApiModel();
    postAM.media = [params.mediaIri];
    postAM.content = params.caption || '';
    if (params.projectIri) {
      postAM.project = params.projectIri;
      postAM.type = PostTypeEnum.PROJECT_MEDIA_POST;
    }
    if (params.eventIri) {
      postAM.event = params.eventIri;
      postAM.type = PostTypeEnum.EVENT_MEDIA_POST;
    }
    return this.postRS.create(postAM).pipe(map((post) => new PostViewModel(post)));
  }
}
